import type { entityVersions } from '@clockwise/clients';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { Check, Eye, EyeOff, History, PencilLine, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { customersApi, usersApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

/**
 * Record history for a customer or employee, in the shape of a document's
 * version history: pick a version, and the detail view below shows the record
 * as it stood then.
 *
 * The picker lives on the Details tab rather than in a tab of its own because
 * the thing being versioned IS the details — a separate tab would show a diff
 * next to a detail view that silently kept showing today's values.
 *
 * Read-only by default. A caller with the admin permission can amend what a
 * past version SAYS — the page passes `onAmend` and the banner offers it — but
 * that opens the ordinary edit form in amend mode rather than making anything
 * here editable. Effective dates are never rewritten and no timesheet is
 * re-pointed (D10), so nothing in this component changes a boundary.
 *
 * Versions are ordered by effective date, NEVER by version number: a backdated
 * correction inserts a mid-timeline range under the next free number, so the
 * numbers are monotonic but not chronological. The server sorts them; this
 * renders them in the order given.
 */

export type EntityKind = 'user' | 'customer';
export type EntityVersion = entityVersions.EntityVersion;

export function useEntityVersions(
  kind: EntityKind,
  id: string,
  /** Skip the request entirely. This endpoint is the most expensive read on
   *  either detail surface — it diffs every consecutive version and resolves
   *  the foreign-key labels server-side — so a page that only sometimes needs
   *  the history should say so rather than pay for it on every load. */
  options: { enabled?: boolean } = {},
): UseQueryResult<{ data: EntityVersion[] }> {
  return useQuery({
    queryKey: kind === 'user' ? queryKeys.users.versions(id) : queryKeys.customers.versions(id),
    queryFn: () => (kind === 'user' ? usersApi.listVersions(id) : customersApi.listVersions(id)),
    enabled: options.enabled ?? true,
  });
}

/**
 * The version being viewed, or null for "current".
 *
 * A `?version=` that doesn't resolve — a stale bookmark, a version pruned
 * away — falls back to current rather than rendering an empty detail view.
 * The current version resolves to null too: viewing it is not time travel, and
 * the read-only banner would be wrong.
 */
export function selectedVersion(
  versions: readonly EntityVersion[] | undefined,
  requested: number | undefined,
): EntityVersion | null {
  if (requested == null || !versions) return null;
  const found = versions.find((v) => v.version === requested);
  return found && !found.isCurrent ? found : null;
}

/**
 * Merge a version's values over the live record, so an existing detail view
 * can render a past version with no changes to how it reads its fields.
 *
 * `values` carries every versioned column under the entity's own API field
 * names, so a plain spread lands them all. Fields the version doesn't cover —
 * `id`, timestamps, server-resolved reference labels — stay as they are, which
 * is why `labelOverrides` exists: a historical `salesRepId` with today's
 * `salesRepName` beside it would name the wrong person.
 */
export function withVersionValues<T extends object>(
  record: T,
  version: EntityVersion | null,
  labelOverrides: (labels: Record<string, string | null>) => Partial<T>,
): T {
  if (!version) return record;
  return { ...record, ...(version.values as Partial<T>), ...labelOverrides(version.labels) };
}

/**
 * The value each changed field held BEFORE this version, keyed by field —
 * what `PreviousValues` renders struck through on the detail rows.
 *
 * Null for the current version: there is nothing to strike through when you
 * are looking at what the record says now.
 */
export function previousValues(
  version: EntityVersion | null,
): Record<string, { value: unknown; label?: string | null }> | null {
  if (!version) return null;
  return Object.fromEntries(
    version.changes.map((change) => [
      change.field,
      // A restricted field is listed as changed but carries no values, so
      // there is nothing to show but the fact of the change.
      change.restricted
        ? { value: '(restricted)' }
        : { value: change.from, label: change.fromLabel },
    ]),
  );
}

/** The 2000 floor every v1 is anchored at (D5). Not a moment anything
 *  happened — a coverage device — so it is never shown as one. */
const VERSION_FLOOR_MS = Date.parse('2000-01-01T00:00:00.000Z');

/**
 * When a version CAME INTO BEING, which is what both the range and the menu
 * label date themselves from.
 *
 * ONE source for every version, so no row is dated differently from its
 * neighbours. For v2 onward this is exactly `effective_from` — the mint and
 * the audit row both take the database's `now()`, so the edit and the version
 * it produced carry the identical instant, and reading the audit row is the
 * same as reading the bound.
 *
 * v1 is the one version where they differ: its bound is the synthetic 2000
 * floor, while its `create` audit row holds the real creation time. Rendering
 * the bound there claimed the record was made at midnight in a year nobody was
 * here for. Null when the audit row has aged out AND the bound is the floor —
 * there is then no honest instant to show, and the callers say so rather than
 * invent one.
 */
function startedAt(version: EntityVersion): string | null {
  const at = version.authors[0]?.at;
  if (at) return at;
  return Date.parse(version.effectiveFrom) === VERSION_FLOOR_MS ? null : version.effectiveFrom;
}

/**
 * "Aug 14, 2026, 2:32 PM – now" / "Jun 7, 9:05 AM – Aug 13, 2026, 4:10 PM".
 *
 * The upper bound is shown verbatim — `effectiveTo` is the moment this version
 * stopped applying, which is the same moment its successor started, so
 * consecutive ranges read as a continuous timeline. (While the bounds were
 * whole dates this had to subtract a day, because an exclusive DATE bound named
 * a day the version did not cover. An exclusive instant names no day at all, so
 * that correction is now the thing that would be wrong.)
 *
 * The lower bound comes from {@link startedAt}, so v1 reads from when the
 * record was created rather than from the 2000 floor.
 *
 * Rendered in the VIEWER's zone, like every other timestamp on these pages.
 * The stored value is UTC; showing it in UTC would date an evening edit to the
 * next day for anyone east of it.
 */
export function effectiveRange(version: EntityVersion): string {
  const to = version.effectiveTo === null ? 'now' : formatInstant(version.effectiveTo);
  const start = startedAt(version);
  return start ? `${formatInstant(start)} – ${to}` : `Since creation – ${to}`;
}

/** "Aug 14, 2026, 2:32 PM" — a version bound, to the minute. Seconds are
 *  dropped: they distinguish two edits made inside the same minute, which the
 *  version numbers beside them already do more legibly. */
function formatInstant(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** "Aahana Garg", "Aahana Garg and 2 others", "Imported" — who moved this
 *  version. Authorship comes from the audit log, so a version whose audit rows
 *  have aged out simply has none. */
export function authorSummary(version: EntityVersion): string | null {
  const [first, ...rest] = version.authors;
  if (!first) return null;
  const name = first.name ?? (first.source === 'ui' ? 'Unknown user' : sourceLabel(first.source));
  if (rest.length === 0) return name;
  return `${name} and ${rest.length} other${rest.length === 1 ? '' : 's'}`;
}

/**
 * The date a version came into being — the same instant {@link effectiveRange}
 * opens with, just without the time.
 *
 * Shares {@link startedAt} so the menu label and the window underneath it can
 * never date the same version differently. Renders in the VIEWER's zone, like
 * the audit tab and the Created/Updated rows; forcing UTC would date the same
 * event a day later for anyone west of it who edits after ~17:00 local.
 */
export function changedOn(version: EntityVersion): string {
  const start = startedAt(version);
  if (!start) return 'At creation';
  return new Date(start).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** "v4 · Manish Balsara" — the headline of a menu row.
 *
 * The NUMBER leads. Two edits on one afternoon now produce two versions with
 * the same displayed date, and the number is the only thing that tells them
 * apart at a glance; the exact window sits on the second line. */
export function versionLabel(version: EntityVersion): string {
  const who = authorSummary(version);
  return who ? `v${version.version} · ${who}` : `v${version.version}`;
}

/**
 * The version whose changes the detail rows should strike through, or null for
 * no diff at all.
 *
 * Two ways to get one: pick a past version from the picker (time travel — the
 * whole page reads as that version), or switch on "what changed" while looking
 * at the CURRENT record. The second shows a diff WITHOUT rewinding any values,
 * which is why it can't just be modelled as selecting the current version.
 *
 * Off by default, and only offered when there is history: a record edited once
 * has nothing to compare against, and an always-on diff would decorate a page
 * most people open to read today's values.
 */
export function diffTarget(
  versions: readonly EntityVersion[] | undefined,
  selected: EntityVersion | null,
  showCurrentDiff: boolean,
): EntityVersion | null {
  if (selected) return selected;
  if (!showCurrentDiff || !versions || versions.length <= 1) return null;
  return versions.find((v) => v.isCurrent) ?? null;
}

function sourceLabel(source: string): string {
  if (source === 'import') return 'Imported';
  if (source === 'system') return 'System';
  return 'API';
}

/**
 * The version picker, plus the "you are looking at the past" banner.
 *
 * The banner is not decoration: without it, a detail page showing a stale
 * address is indistinguishable from one showing the current address, and
 * someone will act on the wrong one.
 */
export function EntityVersionBar({
  query,
  selected,
  onSelect,
  onAmend,
  showCurrentDiff = false,
  onShowCurrentDiffChange,
}: {
  query: UseQueryResult<{ data: EntityVersion[] }>;
  selected: EntityVersion | null;
  onSelect: (version: number | undefined) => void;
  /** Offered in the banner when viewing a past version. Omitted — by the page,
   *  when the caller lacks the admin permission — hides the action entirely.
   *  The page owns where it goes, so this component stays route-agnostic. */
  onAmend?: (version: number) => void;
  /** Whether to show what changed in the CURRENT version. Controlled by the
   *  page because `diffTarget` has to agree with it — the page feeds the same
   *  answer to `previousValues`, and a copy of the flag in here would let the
   *  toggle and the struck-through rows disagree. */
  showCurrentDiff?: boolean;
  onShowCurrentDiffChange?: (next: boolean) => void;
}) {
  if (query.isLoading) return <Skeleton className="ml-auto h-6 w-24" />;

  // A failed fetch must not silently leave the page looking like a record with
  // no history (G15) — especially here, where the absence of a picker is the
  // only thing distinguishing the two.
  if (query.isError) {
    return (
      <p className="text-sm text-destructive">
        Could not load this record's history. Reload the page to try again.
      </p>
    );
  }

  const versions = query.data?.data ?? [];
  // One version means no history: nothing to pick between and nothing to diff
  // against, so neither the picker nor the "what changed" toggle appears.
  if (versions.length <= 1) return null;

  return (
    <div className="space-y-2">
      {/* A quiet text button, right-aligned. Almost nobody opens a record to
          look at its history, so this is sized like a utility link rather than
          a form control — a bordered select the width of the field labels read
          as the first row of the detail table and competed with the data for
          attention. When a past version IS being viewed the banner below
          carries the weight, so the trigger still doesn't need to shout. */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        {/* "What changed" for the CURRENT version. Only offered while looking
            at current — a past version is already showing its diff — and only
            when the page wired the state up. Off by default: most people open
            a record to read what it says today, and a page that arrives
            pre-decorated with strikethroughs answers a question they didn't
            ask.
            
            Switching it on changes nothing but the ROWS: the same struck-
            through previous value and amber tint a past version gets, with no
            banner. A banner here would be claiming something about the values
            below, and there is nothing to claim — they are current. */}
        {onShowCurrentDiffChange && !selected ? (
          <Button
            variant="ghost"
            size="sm"
            aria-pressed={showCurrentDiff}
            onClick={() => onShowCurrentDiffChange(!showCurrentDiff)}
            className="h-auto gap-1.5 px-2 py-1 text-xs font-normal text-muted-foreground hover:text-foreground"
          >
            {showCurrentDiff ? (
              <EyeOff className="size-3.5" aria-hidden="true" />
            ) : (
              <Eye className="size-3.5" aria-hidden="true" />
            )}
            {showCurrentDiff ? 'Hide what changed' : 'Show what changed'}
          </Button>
        ) : null}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-auto gap-1.5 px-2 py-1 text-xs font-normal text-muted-foreground hover:text-foreground"
            >
              <History className="size-3.5" aria-hidden="true" />
              {selected ? `v${selected.version} · ${changedOn(selected)}` : 'History'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
              Changes to this record
            </DropdownMenuLabel>
            {versions.map((version) => {
              const active = version.isCurrent ? selected === null : selected === version;
              return (
                <DropdownMenuItem
                  key={version.version}
                  onSelect={() => onSelect(version.isCurrent ? undefined : version.version)}
                  className="gap-2"
                >
                  <Check
                    className={cn('size-4 shrink-0', active ? 'opacity-100' : 'opacity-0')}
                    aria-hidden="true"
                  />
                  {/* Two lines. The window has to be here — with instants,
                      several versions can share a date and the picker would
                      otherwise offer a column of identical-looking rows — but
                      it is long, so it sits under the headline rather than
                      widening every row. */}
                  <span className="flex flex-col gap-0.5">
                    <span>
                      {versionLabel(version)}
                      {version.isCurrent ? (
                        <span className="ml-1.5 text-muted-foreground">(current)</span>
                      ) : null}
                    </span>
                    <span className="text-xs text-muted-foreground">{effectiveRange(version)}</span>
                  </span>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {selected ? (
        <div className="rounded-md border border-amber-500/50 bg-amber-50 px-3 py-2 text-sm dark:bg-amber-950/30">
          <p>
            Showing this record as it was <strong>{effectiveRange(selected)}</strong>
            {authorSummary(selected) ? <> · {authorSummary(selected)}</> : null}. Values below are
            read-only.
          </p>
          {selected.changes.length > 0 ? (
            <p className="mt-1 text-muted-foreground">
              Changed in this version: {selected.changes.map((c) => c.label).join(', ')}.
            </p>
          ) : selected.isInitial ? (
            <p className="mt-1 text-muted-foreground">The record as first created.</p>
          ) : null}
          <div className="mt-2 flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => onSelect(undefined)}>
              <RotateCcw className="size-4" />
              Back to current
            </Button>
            {/* Only for a CLOSED version: the current one is the record itself,
                which the ordinary Edit button already covers, and the server
                refuses it outright. */}
            {onAmend && !selected.isCurrent ? (
              <Button variant="outline" size="sm" onClick={() => onAmend(selected.version)}>
                <PencilLine className="size-4" />
                Correct this period
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
